import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { DropdownSourceContext } from "@ryde/redux";
import {
  capitalize,
  NO_VALUE_DISPLAY,
  useFormats,
  type IAvailability,
} from "@ryde/shared";

import { formatTimeRange } from "../templates-table/availability-cell-template/availability-cell.helpers";
import {
  type CsvCellTransformerFn,
  type ICsvColumnTransformer,
} from "./models";

export const useCsvAvailabilityCellTransformer = (): {
  getAvailabilityValue: CsvCellTransformerFn;
} => {
  const { t } = useTranslation();
  const { getDropdownItems } = useContext(DropdownSourceContext);
  const { timeFormat } = useFormats();

  const dayGroupLabel = capitalize(t("dayGroup.plural"));
  const dayTypeLabel = capitalize(t("dayType.plural"));

  const getAvailabilityValue = async ({
    value,
  }: ICsvColumnTransformer): Promise<string> => {
    const items = value as IAvailability[] | undefined;

    if (!Array.isArray(items) || !items.length) {
      return NO_VALUE_DISPLAY;
    }

    const [dayTypeItems, dayGroupItems] = await Promise.all([
      getDropdownItems?.("dayType"),
      getDropdownItems?.("dayGroup"),
    ]);

    const parts = items.map(item => {
      const timeRange = formatTimeRange({
        from: item.from,
        to: item.to,
        timeFormat,
      });

      const dayGroupIdsSet = item.dayGroupIds
        ? new Set(item.dayGroupIds)
        : null;
      const dayGroupNames = (dayGroupItems || [])
        .filter(di => dayGroupIdsSet?.has(+di.value))
        .map(di => di.label);

      const dayTypeIdsSet = item.dayTypeIds ? new Set(item.dayTypeIds) : null;
      const dayTypeNames = (dayTypeItems || [])
        .filter(di => dayTypeIdsSet?.has(+di.value))
        .map(di => di.label);

      const segments: string[] = [];

      if (timeRange) {
        segments.push(timeRange);
      }

      if (dayGroupNames.length) {
        segments.push(`${dayGroupLabel}: ${dayGroupNames.join(", ")}`);
      }

      if (dayTypeNames.length) {
        segments.push(`${dayTypeLabel}: ${dayTypeNames.join(", ")}`);
      }

      return segments.join(" ");
    });

    return parts.join(" | ");
  };

  return { getAvailabilityValue };
};
